import {
  getElement,
  elementExists,
  attachEventListener,
  setTransform,
  createInitializer,
} from "../../../js/utils.js";

// The header bundle is loaded asynchronously. Start from page top rather than
// sampling at module-evaluation time so a fast first swipe that happens before
// this file finishes loading is still recognized as downward movement.
let lastScrollY = 0;
let isHeaderHidden = false;

const applyScrolledChrome = (headerElement, scrollY) => {
  // Classes may already be set by the inline `tfn-header-chrome-boot` in
  // functions.php (runs before this module loads).
  headerElement.classList.toggle("scrolling", scrollY > 20);
};

const setHeaderTransform = (element, translateY) => {
  return setTransform(element, `translateY(${translateY}px)`);
};

const getVisibleAdminBarHeight = () => {
  const adminBar = getElement("#wpadminbar");

  if (!elementExists(adminBar)) {
    return 0;
  }

  const { bottom, height } = adminBar.getBoundingClientRect();
  return Math.max(0, Math.min(height, bottom));
};

const syncHeaderTop = (element) => {
  const top = getVisibleAdminBarHeight();
  element.style.setProperty("--tfn-sticky-top", `${top}px`);
  // `scroll-padding-top` has to live on the scroll container, not on the
  // header, so publish the measured height on <html> for style.scss to offset
  // anchor landings by. offsetHeight is layout height and ignores the
  // hide/show translateY, so it stays correct while the header is parked
  // off-screen.
  document.documentElement.style.setProperty(
    "--tfn-header-h",
    `${element.offsetHeight}px`,
  );
  return top;
};

const getHiddenOffset = (element) => {
  const fixedTop = Number.parseFloat(window.getComputedStyle(element).top) || 0;
  return -Math.ceil(element.offsetHeight + Math.max(0, fixedTop));
};

const setHeaderTransformNow = (element, translateY) => {
  const previous = element.style.transition;
  element.style.transition = "none";
  setHeaderTransform(element, translateY);
  void element.offsetHeight;
  element.style.transition = previous;
};

const revealHeader = (headerElement, { immediate = false } = {}) => {
  const write = immediate ? setHeaderTransformNow : setHeaderTransform;
  isHeaderHidden = false;
  write(headerElement, 0);
};

const isMobileMenuOpen = () => document.body.classList.contains("menu-active");

const syncHeaderAfterFreeze = (headerElement) => {
  syncHeaderTop(headerElement);
  lastScrollY = window.scrollY;
  applyScrolledChrome(headerElement, lastScrollY);
  // Chrome device emulation can emit visibility/focus restores immediately
  // after a swipe. Preserve the current hide/show state away from the top so
  // those lifecycle events cannot undo a legitimate downward-scroll hide.
  if (lastScrollY <= 50 && isHeaderHidden) {
    revealHeader(headerElement, { immediate: true });
  }
};

const createScrollHandler = (headerElement) => {
  return () => {
    const currentScrollY = window.scrollY;
    syncHeaderTop(headerElement);

    if (isMobileMenuOpen()) {
      lastScrollY = currentScrollY;
      if (isHeaderHidden !== false) {
        revealHeader(headerElement, { immediate: true });
      }
      return;
    }

    applyScrolledChrome(headerElement, currentScrollY);

    if (currentScrollY === lastScrollY) {
      return;
    }

    const scrollingDown = currentScrollY > lastScrollY;
    const nextHidden = scrollingDown
      ? currentScrollY > 50
        ? true
        : null
      : false;

    if (nextHidden !== null && nextHidden !== isHeaderHidden) {
      isHeaderHidden = nextHidden;
      setHeaderTransform(
        headerElement,
        nextHidden ? getHiddenOffset(headerElement) : 0,
      );
    }

    lastScrollY = currentScrollY;
  };
};

const initializeHeaderScroll = () => {
  const headerElement = getElement(".et-l--header");

  if (elementExists(headerElement)) {
    syncHeaderTop(headerElement);
    const scrollHandler = createScrollHandler(headerElement);

    scrollHandler();

    attachEventListener(document, "visibilitychange", () => {
      if (document.visibilityState === "visible") {
        syncHeaderAfterFreeze(headerElement);
      }
    });
    attachEventListener(window, "pageshow", () => {
      syncHeaderAfterFreeze(headerElement);
    });
    attachEventListener(document, "resume", () => {
      syncHeaderAfterFreeze(headerElement);
    });
    attachEventListener(window, "focus", () => {
      if (document.visibilityState === "visible") {
        syncHeaderAfterFreeze(headerElement);
      }
    });
    attachEventListener(window, "resize", () => {
      syncHeaderTop(headerElement);
      if (isHeaderHidden) {
        setHeaderTransformNow(headerElement, getHiddenOffset(headerElement));
      }
    });

    return attachEventListener(window, "scroll", scrollHandler, {
      passive: true,
    });
  }

  return false;
};

const initializeMobileNav = () => {
  const toggleButton = getElement(".mobile-nav-toggle");
  const bodyElement = getElement("body");
  const htmlElement = getElement("html");
  const headerElement = getElement(".et-l--header");

  const targetElement = elementExists(bodyElement) ? bodyElement : htmlElement;

  if (elementExists(toggleButton) && elementExists(targetElement)) {
    return attachEventListener(toggleButton, "click", (event) => {
      event.preventDefault();
      targetElement.classList.toggle("menu-active");
      if (isMobileMenuOpen() && elementExists(headerElement)) {
        lastScrollY = window.scrollY;
        revealHeader(headerElement, { immediate: true });
      }
    });
  }

  return false;
};

// Pure function to initialize mobile menu dropdowns
const initializeMobileMenuDropdowns = () => {
  const dropdownMenus = document.querySelectorAll(
    ".main-menu .menu-item-has-children",
  );

  if (dropdownMenus.length > 0) {
    dropdownMenus.forEach((dropdown) => {
      dropdown.addEventListener("click", (event) => {
        if (event.target === dropdown) {
          event.stopPropagation();
          dropdown.classList.toggle("active");
        }
      });
    });
    return true;
  }

  return false;
};

/**
 * Logo home link — DTC `/` vs HCP `/hcp/`.
 *
 * Priority:
 * 1. `body.is-hcp` (PHP: page under hcp or /hcp path)
 * 2. URL path contains `/hcp`
 * 3. Visible `.menu-hcp` (module not display:none) vs visible `.main-menu`
 */
const isHcpContext = () => {
  if (document.body.classList.contains("is-hcp")) {
    return true;
  }

  const path = window.location.pathname || "";
  if (/(?:^|\/)hcp(?:\/|$)/.test(path)) {
    return true;
  }

  const hcpMenu = document.querySelector(".et-l--header ul.menu-hcp");
  if (hcpMenu) {
    const hcpModule = hcpMenu.closest(".et_pb_code");
    if (
      hcpModule &&
      window.getComputedStyle(hcpModule).display !== "none"
    ) {
      return true;
    }
  }

  return false;
};

const initializeLogoHomeLink = () => {
  const logoLink = getElement(".et-l--header .main-menu-row .et_pb_image a");

  if (!elementExists(logoLink)) {
    return false;
  }

  logoLink.setAttribute("href", isHcpContext() ? "/hcp/" : "/");
  return true;
};

const initializeMobileUtilityLinks = () => {
  const utilityMenu = getElement(".top-header-menu");
  const ctaButton = getElement(".main-nav-wrap > .btn");

  if (
    elementExists(utilityMenu) &&
    elementExists(ctaButton) &&
    !getElement(".mobile-utility-menu")
  ) {
    const sourceLinks = utilityMenu.querySelectorAll("a");
    const safetyHref = sourceLinks[0]?.getAttribute("href") || "#isi-full";
    const prescribingHref =
      sourceLinks[1]?.getAttribute("href") || "/wp-content/uploads/Placeholder-PDF.pdf";
    // Mirrors the authored desktop utility menu. The patient-site href has no
    // fallback on purpose: it used to default to a hard-coded Nelkarmi domain,
    // which pointed this site's mobile menu at another project. With no third
    // utility link authored there is nothing correct to link to, so the row is
    // omitted rather than shipped pointing somewhere wrong.
    const patientHref = sourceLinks[2]?.getAttribute("href");
    const externalIcon = sourceLinks[2]?.querySelector("svg")?.cloneNode(true);
    const mobileLinks = [
      { text: "Important Safety Information", href: safetyHref },
      { text: "Prescribing Information", href: prescribingHref, newWindow: true },
      { text: "Patient Information", href: prescribingHref, newWindow: true },
      ...(patientHref
        ? [{ text: "Patient site", href: patientHref, newWindow: true, external: true }]
        : []),
    ];
    const mobileMenu = document.createElement("menu");

    mobileMenu.className = "mobile-utility-menu";
    mobileLinks.forEach((linkData) => {
      const item = document.createElement("li");
      const link = document.createElement("a");

      link.href = linkData.href;
      link.textContent = linkData.text;

      if (linkData.newWindow) {
        link.target = "_blank";
        link.rel = "noopener";
      }

      if (linkData.external && externalIcon) {
        link.append(" ", externalIcon.cloneNode(true));
      }

      item.appendChild(link);
      mobileMenu.appendChild(item);
    });

    ctaButton.insertAdjacentElement("afterend", mobileMenu);
    return true;
  }

  return false;
};

// Wire up header behaviours. main.js dynamic-imports this module inside
// DOMContentLoaded, so the DOM is already ready when we run.
const initializeHeader = () => {
  initializeHeaderScroll();
  initializeMobileNav();
  initializeMobileMenuDropdowns();
  initializeLogoHomeLink();
  initializeMobileUtilityLinks();
};

createInitializer(initializeHeader)();
